import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Link as LinkIcon, Copy, ExternalLink, Plus, TrendingUp, ArrowRight, MoreVertical, Edit, Copy as CopyIcon, Archive, Trash2, Calendar, X, Globe, Link2, PlayCircle, RefreshCw, ChevronDown, ChevronUp, CheckCircle, QrCode } from 'lucide-react';
import { useLinks, useCreateLink, useDeleteLink, useUpdateLinkStatus, useUpdateLink } from '../hooks/useLinks';
import { useBrands } from '../hooks/useBrands';
import { Link, linksService, type LinkStatus, type UpdateLinkPayload } from '../services/linksService';
import { Card } from '../components/common/Card';
import { BrandDropdown } from '../components/common/BrandDropdown';
import { formatLocaleDate } from '../utils/dateUtils';
import type { Brand } from '../types';
import { useAuthStore } from '../store/authStore';
import { auth } from '../config/firebase'; // Import auth for getIdToken
import { QRCodeModal } from '../components/modals';
import LinksMetrics from '../components/analytics/LinksMetrics';

// Helper function to get status color classes
const getStatusColorClasses = (status: string) => {
  switch (status.toLowerCase()) {
    case 'active':
      return 'bg-green-100 text-green-700 border-green-200';
    case 'inactive':
      return 'bg-red-100 text-red-700 border-red-200';
    case 'pending':
      return 'bg-orange-100 text-orange-700 border-orange-200';
    case 'expired':
      return 'bg-gray-100 text-gray-700 border-gray-200';
    default:
      return 'bg-blue-100 text-blue-700 border-blue-200';
  }
};

// Helper function to truncate URL
const truncateUrl = (url: string, maxLength: number = 60) => {
  if (url.length <= maxLength) return url;
  return url.substring(0, maxLength) + '...';
};

// Link card component
const LinkCard: React.FC<{ 
  link: Link; 
  onDelete: (linkId: string) => void;
  isDeleting: boolean;
  onDuplicate: (link: Link) => void;
  onEditRequest: (link: Link) => void;
  onQRCodeRequest: (link: Link) => void;
  onUpdateStatus: (linkId: string, newStatus: LinkStatus) => void;
  isUpdatingStatus?: boolean;
}> = ({ link, onDelete, isDeleting, onDuplicate, onEditRequest, onQRCodeRequest, onUpdateStatus, isUpdatingStatus }) => {
  const { t, i18n } = useTranslation();
  const [copied, setCopied] = useState(false);
  const [showActions, setShowActions] = useState(false);

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(link.newURL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy URL:', error);
    }
  };

  const handleOpenUrl = () => {
    window.open(link.newURL, '_blank');
  };

  const handleEdit = () => {
    onEditRequest(link);
    setShowActions(false);
  };

  const handleQRCode = () => {
    onQRCodeRequest(link);
    setShowActions(false);
  };

  const handleDuplicate = () => {
    onDuplicate(link);
    setShowActions(false);
  };

  const handleToggleArchiveStatus = () => {
    const newStatus = link.status === 'archived' ? 'active' : 'archived';
    onUpdateStatus(link.id, newStatus);
    setShowActions(false);
  };

  const handleDelete = () => {
    onDelete(link.id);
    setShowActions(false);
  };

  const isArchived = link.status === 'archived';
  const buttonText = isArchived ? t('common.activate', 'Activate') : t('common.archive', 'Archive');
  const loadingText = isArchived ? t('common.activating', 'Activating...') : t('common.archiving', 'Archiving...');

  return (
    <Card className={`relative ${isDeleting || isUpdatingStatus ? 'pointer-events-none' : ''}`}>
      {(isDeleting || isUpdatingStatus) && (
        <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10 rounded-xl">
          <div className={`flex items-center gap-3 ${isDeleting ? 'text-red-600' : 'text-gray-600'}`}>
            <div className={`w-6 h-6 border-2 ${isDeleting ? 'border-red-600' : 'border-gray-600'} border-t-transparent rounded-full animate-spin`} />
            <span className="text-sm font-medium">
              {isDeleting ? t('common.deleting', 'Deleting...') : loadingText}
            </span>
          </div>
        </div>
      )}
      
      {/* Main wrapper for card content: Logo, Textual Info, and Actions Menu */}
      <div className={`flex items-start gap-4 ${isDeleting || isUpdatingStatus ? 'opacity-50' : ''} relative pr-14`}>
        {/* Brand logo */}
        <div className="flex-shrink-0">
          <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 border border-black">
            <img 
              src={link.brandLogo} 
              alt={link.brandName}
              title={link.brandName}
              className="w-full h-full object-cover"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(link.brandName)}&background=f3f4f6&color=6b7280&size=48`;
              }}
            />
          </div>
        </div>

        {/* Main textual content block (URLs, clicks, footer) */}
        <div className="flex-1 min-w-0">
          {/* Section for URLs and Click Count */}
          <div className="flex flex-col items-start gap-3 mb-3 md:flex-row md:items-center md:gap-4">
            {/* URL Block (Short & Original) - Takes available width */}
            <div className="flex-1 w-full">
              {/* Short URL + Action Buttons (Copy, Open) Container */}
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 mb-2">
                <a 
                  href={link.newURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-lg font-medium text-primary-600 break-all hover:underline cursor-pointer"
                >
                  {link.newURL}
                </a>
                {/* Group for Copy and Open URL buttons */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={handleCopyUrl}
                    className={`w-8 h-8 flex items-center justify-center rounded border transition-colors flex-shrink-0 ${
                      copied 
                        ? 'bg-green-50 hover:bg-green-100 border-green-200' 
                        : 'bg-primary-50 hover:bg-primary-100 border-primary-200'
                    }`}
                    title={copied ? t('common.copied', 'Copied!') : t('links.copyUrl', 'Copy URL')}
                  >
                    {copied ? (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    ) : (
                      <Copy className="w-4 h-4 text-primary-600" />
                    )}
                  </button>
                </div>
              </div> 
              
              {/* Original URL */}
              <div className="mb-2">
                <div className="flex items-center gap-2">
                  <ArrowRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <a 
                    href={link.oldURL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-gray-500 break-all hover:underline hover:text-gray-700 cursor-pointer transition-colors"
                  >
                    {link.oldURL}
                  </a>
                </div>
              </div>
            </div>

            {/* Click count box - full width on mobile, auto on desktop */}
            <div className="flex-shrink-0 md:self-center w-full md:w-auto">
              <div className="flex items-center justify-center gap-1 px-2.5 py-1 bg-gray-50 border border-black rounded-lg">
                <TrendingUp className="w-4 h-4 text-gray-600" />
                <span className="text-lg font-semibold text-gray-900">{link.clickCount.toLocaleString()}</span>
                <span className="text-sm text-gray-500">{t('links.clicks', 'clicks')}</span>
              </div>
            </div>
          </div> {/* End of URLs and Click Count section */}

          {/* Footer section (Created by, Dates, Status) */}
          <div className="flex flex-row items-start justify-between md:items-center gap-3 pt-3 border-t border-gray-100">
            {/* Left part of footer: Created by and Date info */}
            <div className="flex flex-col items-start gap-3 md:flex-row md:items-center">
              {/* Created by info */}
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full overflow-hidden bg-gray-100">
                  <img 
                    src={link.userPicture} 
                    alt={link.userName}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(link.userName)}&background=f3f4f6&color=6b7280&size=24`;
                    }}
                  />
                </div>
                <div>
                  <p className="text-xs text-gray-500">{t('links.createdBy', 'Created by')}</p>
                  <p className="text-xs font-medium text-gray-700">{link.userName}</p>
                </div>
              </div>
              
              {/* Date information (Last clicked, Created on) */}
              <div>
                {link.lastClicked ? (
                  <div className="flex items-center gap-1 text-xs text-gray-500">
                    <Calendar className="w-3 h-3" />
                    {t('links.lastClicked', 'Last clicked')} {formatLocaleDate(link.lastClicked, i18n.language, { dateFormat: 'PP' })}
                  </div>
                ) : (
                  <div className="h-4">{/* Placeholder for alignment */}</div>
                )}
                <div className="flex items-center gap-1 text-xs text-gray-500">
                  <Calendar className="w-3 h-3" />
                  Created on {formatLocaleDate(link.createdAt, i18n.language, { dateFormat: 'PP' })}
                </div>
              </div>
            </div>
            
            {/* Status badge - aligned to end on desktop, stacks on mobile */}
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColorClasses(link.status)}`}
            >
              {t(`links.statuses.${link.status}`, link.status)}
            </span>
          </div>
        </div> {/* End of Main textual content block (flex-1 min-w-0) */} 

        {/* Actions menu (Three Dots) - Positioned absolutely to top right of the parent div.flex.items-start */}
        <div className="absolute top-2 right-4">
          <button
            onClick={() => setShowActions(!showActions)}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <MoreVertical className="w-5 h-5 text-gray-500" />
          </button>
          {showActions && (
            <>
              <div 
                className="fixed inset-0 z-10"
                onClick={() => setShowActions(false)}
              />
              <div className="absolute right-0 mt-2 w-48 rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 z-20">
                <div className="py-1">
                  <button
                    onClick={handleEdit}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <Edit className="w-4 h-4 mr-3" />
                    {t('common.edit', 'Edit')}
                  </button>
                  <button
                    onClick={handleQRCode}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <QrCode className="w-4 h-4 mr-3" />
                    {t('qrCode.title', 'QR Code')}
                  </button>
                  <button
                    onClick={handleDuplicate}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  >
                    <CopyIcon className="w-4 h-4 mr-3" />
                    {t('common.duplicate', 'Duplicate')}
                  </button>
                  <button
                    onClick={handleToggleArchiveStatus}
                    disabled={isDeleting || isUpdatingStatus}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isUpdatingStatus ? (
                      <div className="w-4 h-4 mr-3 border-2 border-gray-600 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      isArchived ? <PlayCircle className="w-4 h-4 mr-3 text-green-600" /> : <Archive className="w-4 h-4 mr-3" />
                    )}
                    {isUpdatingStatus ? loadingText : buttonText}
                  </button>
                  <button
                    onClick={handleDelete}
                    disabled={isDeleting || isUpdatingStatus}
                    className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isDeleting ? (
                      <div className="w-4 h-4 mr-3 border-2 border-red-600 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4 mr-3" />
                    )}
                    {isDeleting ? t('common.deleting', 'Deleting...') : t('common.delete', 'Delete')}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div> {/* End of Main wrapper for card content */}
    </Card>
  );
};

// Create Smart Link Modal Component
const CreateSmartLinkModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: { destinationUrl: string; shortCode: string; brandId?: string }) => void;
  brands: Brand[];
  isCreating: boolean;
  initialLinkData?: Link | null;
  isEditMode: boolean;
}> = ({ isOpen, onClose, onSubmit, brands, isCreating, initialLinkData, isEditMode }) => {
  const { t } = useTranslation();
  
  // destinationUrl state will always hold the BASE URL without UTMs
  const [destinationUrl, setDestinationUrl] = useState(''); 
  const [shortCode, setShortCode] = useState('');
  const [selectedBrandId, setSelectedBrandId] = useState<string>('');

  const [debouncedShortCode, setDebouncedShortCode] = useState('');
  const [isCheckingSlug, setIsCheckingSlug] = useState(false);
  const [slugCheckResult, setSlugCheckResult] = useState<{ available: boolean; message: string; isError?: boolean } | null>(null);

  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [utmSource, setUtmSource] = useState('');
  const [utmMedium, setUtmMedium] = useState('');
  const [utmCampaign, setUtmCampaign] = useState('');
  const [utmTerm, setUtmTerm] = useState('');
  const [utmContent, setUtmContent] = useState('');

  // This state will hold the URL displayed in the input (base + UTMs)
  const [liveUrlToDisplay, setLiveUrlToDisplay] = useState('');
  const [copiedDestinationUrl, setCopiedDestinationUrl] = useState(false);
  
  const generateShortCode = useCallback(() => {
    const randomString = Math.random().toString(36).substring(2, 8);
    setShortCode(randomString);
    setSlugCheckResult(null);
    setDebouncedShortCode(randomString);
  }, []);

  const handleBrandSelect = (brandId: string) => {
    setSelectedBrandId(brandId);
  };

  const parseUtmParams = useCallback((url: string): { baseUrl: string; utms: Record<string, string> } => {
    const params: Record<string, string> = {}; // Ensure params is always initialized
    let parsedBaseUrl = url;

    try {
      const fullUrl = (!url.startsWith('http://') && !url.startsWith('https://') && url.includes('.')) ? `https://${url}` : url;
      const urlObj = new URL(fullUrl);
      
      let pathSegmentForBase = urlObj.pathname;

      if (urlObj.pathname === '/' && !urlObj.search && !urlObj.hash) {
        const hostPart = urlObj.hostname;
        let postHostOriginalInput = '';
        const protoStrippedUrl = fullUrl.replace(/^https?:\/\//, '');
        const hostIndex = protoStrippedUrl.indexOf(hostPart);

        if (hostIndex !== -1) {
            const afterHost = protoStrippedUrl.substring(hostIndex + hostPart.length);
            postHostOriginalInput = afterHost.split('?')[0].split('#')[0];
        }

        if (postHostOriginalInput === '' || postHostOriginalInput === '/') {
          pathSegmentForBase = ''; 
        }
      }
      
      // Start with origin + corrected path + hash
      parsedBaseUrl = `${urlObj.origin}${pathSegmentForBase}${urlObj.hash}`;

      // Now, correctly handle search parameters from the original input URL
      const nonUtmQueryParts = new URLSearchParams();
      urlObj.searchParams.forEach((value, key) => {
        if (key.startsWith('utm_')) {
          params[key] = value; // Populate UTM params for return
        } else {
          nonUtmQueryParts.append(key, value); // Collect non-UTM params
        }
      });

      const nonUtmQueryString = nonUtmQueryParts.toString();
      if (nonUtmQueryString) {
        parsedBaseUrl += `?${nonUtmQueryString}`; // Append non-UTM params to the parsedBaseUrl
      }

      // console.log('[parseUtmParams] Input:', url, '| Parsed Base:', parsedBaseUrl, '| Path Segment:', pathSegmentForBase, '| UTMs:', params);
    } catch (e) {
      console.warn("[parseUtmParams] Error parsing URL:", url, e);
      // Fallback for invalid URLs: try to extract UTMs if there's a query string
      const qIndex = url.indexOf('?');
      if (qIndex !== -1) {
        parsedBaseUrl = url.substring(0, qIndex); // Base is before '?'
        const queryString = url.substring(qIndex + 1);
        try {
          const searchPs = new URLSearchParams(queryString);
          const nonUtmParts = new URLSearchParams();
          searchPs.forEach((value, key) => {
            if (key.startsWith('utm_')) {
              params[key] = value;
            } else {
              nonUtmParts.append(key, value);
            }
          });
          const nonUtmQueryString = nonUtmParts.toString();
          if (nonUtmQueryString) {
            parsedBaseUrl += `?${nonUtmQueryString}`;
          }
        } catch (e2) { /* Query parsing failed, params might be empty */ }
      } else {
        // No query string, the whole URL is considered the base, no UTMs extracted here
        parsedBaseUrl = url;
      }
      // console.log('[parseUtmParams] Fallback - Input:', url, '| Parsed Base:', parsedBaseUrl, '| UTMs:', params);
    }
    return { baseUrl: parsedBaseUrl, utms: params };
  }, []);

  // Effect to build the live URL for display
  useEffect(() => {
    // console.log('[useEffect liveUrlToDisplay] Start. destinationUrl:', destinationUrl);
    let base = destinationUrl.trim();
    const utmParams = new URLSearchParams();
    if (utmSource) utmParams.append('utm_source', utmSource);
    if (utmMedium) utmParams.append('utm_medium', utmMedium);
    if (utmCampaign) utmParams.append('utm_campaign', utmCampaign);
    if (utmTerm) utmParams.append('utm_term', utmTerm);
    if (utmContent) utmParams.append('utm_content', utmContent);
    const utmQuery = utmParams.toString();

    if (!base && !utmQuery) {
      setLiveUrlToDisplay('');
      return;
    }

    if (!base && utmQuery) {
      setLiveUrlToDisplay(`?${utmQuery}`);
      return;
    }

    try {
      const parseableBase = (base.startsWith('http://') || base.startsWith('https://') || !base.includes('.') || base.startsWith('/'))
        ? base
        : `https://${base}`;
      // console.log('[useEffect liveUrlToDisplay] base:', base, '| parseableBase:', parseableBase);

      const urlObj = new URL(parseableBase);
      // console.log('[useEffect liveUrlToDisplay] urlObj.pathname:', urlObj.pathname);
      
      const existingParams = new URLSearchParams(urlObj.search);
      const finalParams = new URLSearchParams();

      existingParams.forEach((value, key) => {
        if (!key.startsWith('utm_')) {
          finalParams.append(key, value);
        }
      });

      utmParams.forEach((value, key) => {
        finalParams.set(key, value);
      });
      
      urlObj.search = finalParams.toString();
      let newDisplayUrl = urlObj.toString();

      if (!base.startsWith('http://') && !base.startsWith('https://') && parseableBase.startsWith('https://') && newDisplayUrl.startsWith('https://')) {
        newDisplayUrl = newDisplayUrl.substring(8);
      }
      
      const originalBasePath = base.split('?')[0].split('#')[0];
      // console.log('[useEffect liveUrlToDisplay] originalBasePath:', originalBasePath, '| current newDisplayUrl:', newDisplayUrl);
      if (urlObj.pathname === '/' && 
          !originalBasePath.endsWith('/') && 
          !urlObj.search && 
          !urlObj.hash &&
          newDisplayUrl.endsWith('/')) {
        // console.log('[useEffect liveUrlToDisplay] Removing trailing slash.');
        newDisplayUrl = newDisplayUrl.substring(0, newDisplayUrl.length - 1);
      }
      
      if (finalParams.toString() === '' && newDisplayUrl.endsWith('?')) {
        newDisplayUrl = newDisplayUrl.substring(0, newDisplayUrl.length - 1);
      }
      // console.log('[useEffect liveUrlToDisplay] Final newDisplayUrl before set:', newDisplayUrl.replace(/&$/, ''));
      setLiveUrlToDisplay(newDisplayUrl.replace(/&$/, ''));

    } catch (error) {
      console.warn("[useEffect liveUrlToDisplay] Error constructing live URL:", error);
      const separator = base.includes('?') ? '&' : '?';
      setLiveUrlToDisplay(utmQuery ? `${base}${separator}${utmQuery}` : base);
    }
  }, [destinationUrl, utmSource, utmMedium, utmCampaign, utmTerm, utmContent]);

  useEffect(() => { // Debounce for shortCode
    const handler = setTimeout(() => {
      setDebouncedShortCode(shortCode.trim());
    }, 750);
    return () => clearTimeout(handler);
  }, [shortCode]);

  useEffect(() => { // Slug availability check
    if (!isOpen) { /* ... */ return; }
    if (debouncedShortCode) { /* ... */ } else { setSlugCheckResult(null); }
  }, [debouncedShortCode, isOpen, t]);
  
  useEffect(() => { // Main initialization effect
    if (isOpen) {
      // If a create/update operation is in progress for a *new* link, don't reset form fields.
      // For edit mode, initialLinkData will be present, so this check primarily affects new link creation.
      if (isCreating && !initialLinkData && !isEditMode) {
        return;
      }

      setSlugCheckResult(null);
      setIsCheckingSlug(false);
      setShowAdvancedOptions(false);
      setUtmSource(''); setUtmMedium(''); setUtmCampaign(''); setUtmTerm(''); setUtmContent('');
      setCopiedDestinationUrl(false);

      if (initialLinkData) {
        const { baseUrl, utms } = parseUtmParams(initialLinkData.oldURL);
        setDestinationUrl(baseUrl);
        
        if (Object.keys(utms).length > 0) {
          setUtmSource(utms.utm_source || '');
          setUtmMedium(utms.utm_medium || '');
          setUtmCampaign(utms.utm_campaign || '');
          setUtmTerm(utms.utm_term || '');
          setUtmContent(utms.utm_content || '');
          setShowAdvancedOptions(true);
        }
        
        const newURLParts = initialLinkData.newURL.split('/');
        const originalSlug = newURLParts[newURLParts.length - 1];

        if (isEditMode) {
          setShortCode(originalSlug || ''); // In edit mode, use the exact original slug
          setDebouncedShortCode(originalSlug || '');
        } else { // Duplicating
          const randomSuffix = Math.random().toString(36).substring(2, 9);
          const newShortCodeValue = originalSlug ? `${originalSlug}-copy` : randomSuffix;
          setShortCode(newShortCodeValue);
          setDebouncedShortCode(newShortCodeValue);
        }
        setSelectedBrandId(initialLinkData.brandId || '');
      } else {
        // New link being opened (not duplicate or edit)
        setDestinationUrl('');
        generateShortCode(); // This sets shortCode and debouncedShortCode
      }
    }
  }, [isOpen, initialLinkData, isEditMode, generateShortCode, parseUtmParams, isCreating]);

  // Separate useEffect for default brand selection (from previous brand flicker fix)
  useEffect(() => {
    // Set default brand only if modal is open for a NEW link, no brand is currently selected, and brands are available.
    if (isOpen && !initialLinkData && !selectedBrandId && brands.length > 0 && brands[0]?.id) {
      setSelectedBrandId(brands[0].id);
    }
  }, [isOpen, initialLinkData, selectedBrandId, brands]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (destinationUrl.trim() === '' || shortCode.trim() === '') return; 
    // For edit mode, slugCheckResult might not be relevant if slug is non-editable
    if (!isEditMode && slugCheckResult && !slugCheckResult.available) {
      alert(t('links.errors.slugTakenOnSubmit'));
      return;
    }
    onSubmit({ destinationUrl: liveUrlToDisplay, shortCode: shortCode.trim(), brandId: selectedBrandId });
  };

  const handleClose = () => {
    setDestinationUrl('');
    setShortCode('');
    setSelectedBrandId('');
    setSlugCheckResult(null);
    setIsCheckingSlug(false);
    setDebouncedShortCode('');
    setShowAdvancedOptions(false);
    setUtmSource(''); setUtmMedium(''); setUtmCampaign(''); setUtmTerm(''); setUtmContent('');
    setLiveUrlToDisplay(''); // Clear the live display URL
    setCopiedDestinationUrl(false);
    onClose();
  };
  
  const handleCopyDestinationUrl = () => {
    if (liveUrlToDisplay) {
      navigator.clipboard.writeText(liveUrlToDisplay);
      setCopiedDestinationUrl(true);
      setTimeout(() => setCopiedDestinationUrl(false), 2000);
    }
  };
  
  const generateRandomString = (length: number) => Math.random().toString(36).substring(2, 2 + length);
  
  if (!isOpen) return null;

  const modalTitle = isEditMode ? t('links.editSmartLink', 'Edit Smart Link') : t('links.createSmartLink', 'Create a smart link');
  const submitButtonText = isEditMode 
    ? (isCreating ? t('common.saving', 'Saving...') : t('common.saveChanges', 'Save Changes')) 
    : (isCreating ? t('common.creating', 'Creating...') : t('links.createLinkButton', 'Create Link'));

  const canSubmit = destinationUrl.trim() !== '' && 
                    shortCode.trim() !== '' && 
                    !isCreating && // Now correctly reflects overall processing state
                    (isEditMode || isCheckingSlug === false) && 
                    (isEditMode || slugCheckResult === null || slugCheckResult.available);

  const shouldApplyUtmStyling = utmSource || utmMedium || utmCampaign || utmTerm || utmContent;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" onClick={handleClose}>
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-25" aria-hidden="true" /> 
        <div 
          className="relative bg-white rounded-xl border-2 border-black shadow-xl w-full max-w-lg p-6"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Modal Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold flex items-center gap-2 text-gray-900">
              <Link2 className="w-5 h-5 text-primary-600" />
              {modalTitle}
            </h2>
            <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
              <X size={20} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Destination URL */}
            <div>
              <label htmlFor="destination-url-input" className="flex items-center text-sm font-medium text-gray-700 mb-1">
                <Globe className="w-4 h-4 mr-1.5 text-gray-400" />
                {t('links.destinationUrlLabel', 'Destination URL')}
              </label>
              <div className="relative">
                <textarea
                  id="destination-url-input"
                  value={liveUrlToDisplay}
                  onChange={(e) => {
                    // console.log('[onChange destination-url-input] e.target.value:', e.target.value);
                    const { baseUrl, utms } = parseUtmParams(e.target.value);
                    // console.log('[onChange destination-url-input] parsed baseUrl:', baseUrl, '| parsed utms:', utms);
                    setDestinationUrl(baseUrl);
                    setUtmSource(utms.utm_source || '');
                    setUtmMedium(utms.utm_medium || '');
                    setUtmCampaign(utms.utm_campaign || '');
                    setUtmTerm(utms.utm_term || '');
                    setUtmContent(utms.utm_content || '');
                  }}
                  placeholder={t('links.destinationUrlPlaceholder', 'https://www.socialness.ai/features/ai-link-builder')}
                  className={`w-full text-sm rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent shadow-sm disabled:opacity-50 disabled:cursor-not-allowed border placeholder-gray-400 resize-none overflow-hidden ${ // Base styling
                    shouldApplyUtmStyling 
                      ? 'bg-primary-50 !border-primary-600 text-gray-700' // UTM active specific styling (border, bg)
                      : 'border-gray-300' // Default border
                  } ${ // Padding logic - default p-2.5 seems to match the screenshot better for the purple box.
                    liveUrlToDisplay ? 'p-2.5 pr-10' : 'p-2.5' // Apply base padding, add pr-10 if copy button visible
                  }`}
                  rows={shouldApplyUtmStyling ? 3 : 1}
                  required
                  disabled={isCreating} // isCreating now correctly reflects processing state for both modes
                />
                {/* Show copy button if there's a URL to copy */}
                {liveUrlToDisplay && (
                    <button
                        type="button"
                        onClick={handleCopyDestinationUrl}
                        className="absolute top-1/2 right-2 transform -translate-y-1/2 p-1 text-primary-600 hover:text-primary-800 focus:outline-none"
                        title={copiedDestinationUrl ? t('common.copied', 'Copied!') : t('common.copy', 'Copy')}
                    >
                        {copiedDestinationUrl ? (
                        <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                        <CopyIcon className="w-5 h-5" />
                        )}
                    </button>
                )}
              </div>
            </div>

            {/* Short Link */}
            <div>
              <label htmlFor="short-code-input" className="block text-sm font-medium text-gray-700 mb-1">
                {t('links.shortLinkLabel', 'Short Link')}
              </label>
              <div className="flex items-center">
                <span className="inline-flex items-center px-3 h-[38px] border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm rounded-l-md">
                  go.socialness.ai/
                </span>
                <input
                  type="text"
                  id="short-code-input"
                  value={shortCode}
                  onChange={(e) => {
                    setShortCode(e.target.value);
                    if (slugCheckResult) setSlugCheckResult(null);
                  }}
                  placeholder={t('links.shortCodePlaceholder', 'custom-slug')} 
                  className="flex-1 block w-full px-3 h-[38px] border border-gray-300 focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm placeholder-gray-400 disabled:opacity-50 disabled:cursor-not-allowed"
                  required
                  disabled={isCreating || isEditMode} // Disable if processing OR if in edit mode (shortcode non-editable)
                  minLength={3}
                />
                <button
                  type="button"
                  onClick={generateShortCode}
                  disabled={isCreating || isEditMode} // Disable if processing OR if in edit mode (shortcode non-editable)
                  className="inline-flex items-center px-3 h-[38px] border border-l-0 border-gray-300 bg-gray-50 hover:bg-gray-100 text-gray-700 text-sm rounded-r-md disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                >
                  {t('links.generateButton', 'Generate')}
                </button>
              </div>
              {/* Slug Availability Message - Hide in edit mode */}
              {!isEditMode && (
                <div className="h-5 text-xs px-1 mt-1">
                  {isCheckingSlug && <p className="text-gray-500">{t('links.checkingSlug', 'Checking availability...')}</p>}
                  {slugCheckResult && !isCheckingSlug && (
                    <p className={`${slugCheckResult.isError || !slugCheckResult.available ? 'text-red-600' : 'text-green-600'}`}>
                      {slugCheckResult.message}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Brand Dropdown */}
            <div>
              <label htmlFor="brand-select-modal" className="block text-sm font-medium text-gray-700 mb-1">
                {t('links.brandLabel', 'Brand')}
              </label>
              <BrandDropdown
                brands={brands}
                selectedBrandId={selectedBrandId}
                onBrandSelect={handleBrandSelect}
                isLoading={isCreating} // isCreating now correctly reflects processing state for both modes
                className="h-[38px]"
              />
            </div>
            
            {/* Advanced Options / UTM Parameters Toggle */}
            <div className="border-t border-gray-200 pt-4 mt-4">
              <button
                type="button"
                onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
                className="flex items-center justify-between w-full text-sm font-medium text-gray-700 hover:text-primary-600"
              >
                <span>{t('links.advancedOptions', 'Advanced Options')}</span>
                {showAdvancedOptions ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
              </button>
            </div>

            {/* UTM Parameters Section (remains the same as previously styled) */}
            {showAdvancedOptions && (
              <div className="pt-3">
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  {t('links.utmParameters', 'UTM Parameters:')}
                </label>
                <div className="p-3 border border-gray-300 rounded-md bg-gray-50 space-y-2">
                  {/* utm_source */}
                  <div className="flex items-center">
                    <label 
                      htmlFor="utm-source"
                      className="w-1/3 text-xs text-gray-600 truncate pr-2"
                    >
                      {t('links.source', 'Source (utm_source)')}:
                    </label>
                    <input 
                      type="text" id="utm-source" value={utmSource} onChange={(e) => setUtmSource(e.target.value)}
                      placeholder="e.g., newsletter, google"
                      className="w-2/3 p-1.5 border border-gray-300 rounded-md text-xs focus:ring-1 focus:ring-primary-500 focus:border-primary-500 placeholder-gray-400"
                      disabled={isCreating} // isCreating now correctly reflects processing state
                    />
                  </div>
                  {/* utm_medium */}
                  <div className="flex items-center">
                    <label htmlFor="utm-medium" className="w-1/3 text-xs text-gray-600 truncate pr-2">{t('links.medium', 'Medium (utm_medium)')}:</label>
                    <input type="text" id="utm-medium" value={utmMedium} onChange={(e) => setUtmMedium(e.target.value)} placeholder="e.g., email, cpc" className="w-2/3 p-1.5 border border-gray-300 rounded-md text-xs focus:ring-1 focus:ring-primary-500 focus:border-primary-500 placeholder-gray-400" disabled={isCreating} />
                  </div>
                  {/* utm_campaign */}
                  <div className="flex items-center">
                    <label htmlFor="utm-campaign" className="w-1/3 text-xs text-gray-600 truncate pr-2">{t('links.campaign', 'Campaign (utm_campaign)')}:</label>
                    <input type="text" id="utm-campaign" value={utmCampaign} onChange={(e) => setUtmCampaign(e.target.value)} placeholder="e.g., summer_sale" className="w-2/3 p-1.5 border border-gray-300 rounded-md text-xs focus:ring-1 focus:ring-primary-500 focus:border-primary-500 placeholder-gray-400" disabled={isCreating} />
                  </div>
                  {/* utm_term */}
                  <div className="flex items-center">
                    <label htmlFor="utm-term" className="w-1/3 text-xs text-gray-600 truncate pr-2">{t('links.term', 'Term (utm_term)')}:</label>
                    <input type="text" id="utm-term" value={utmTerm} onChange={(e) => setUtmTerm(e.target.value)} placeholder="e.g., running+shoes" className="w-2/3 p-1.5 border border-gray-300 rounded-md text-xs focus:ring-1 focus:ring-primary-500 focus:border-primary-500 placeholder-gray-400" disabled={isCreating} />
                  </div>
                  {/* utm_content */}
                  <div className="flex items-center">
                    <label htmlFor="utm-content" className="w-1/3 text-xs text-gray-600 truncate pr-2">{t('links.content', 'Content (utm_content)')}:</label>
                    <input type="text" id="utm-content" value={utmContent} onChange={(e) => setUtmContent(e.target.value)} placeholder="e.g., logolink, textlink" className="w-2/3 p-1.5 border border-gray-300 rounded-md text-xs focus:ring-1 focus:ring-primary-500 focus:border-primary-500 placeholder-gray-400" disabled={isCreating} />
                  </div>
                </div>
              </div>
            )}
            
            {/* Action Buttons - Footer of the modal content */}
            <div className="flex justify-end items-center gap-3 pt-5 border-t border-gray-200 mt-1">
              <button
                type="button"
                onClick={handleClose}
                disabled={isCreating} // isCreating now correctly reflects processing state for both modes
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                type="submit"
                disabled={!canSubmit} // canSubmit logic now considers isEditMode
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-primary-500 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isCreating ? ( 
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    {/* submitButtonText already handles Creating.../Saving... based on isCreating and isEditMode */}
                    {isEditMode ? t('common.saving', 'Saving...') : t('common.creating', 'Creating...')}
                  </>
                ) : (
                  submitButtonText // This will show "Create Link" or "Save Changes"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

const Links: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedBrand, setSelectedBrand] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [deletingLinkId, setDeletingLinkId] = useState<string | null>(null);
  const [activeLinkData, setActiveLinkData] = useState<Link | null>(null);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [updatingStatusLinkId, setUpdatingStatusLinkId] = useState<string | null>(null);
  const [isQRCodeModalOpen, setIsQRCodeModalOpen] = useState(false);
  const [qrCodeLink, setQRCodeLink] = useState<Link | null>(null);
  
  const { data: linksData, isLoading, error, refetch: refetchLinks } = useLinks();
  const { data: brandsData, isLoading: brandsLoading } = useBrands();
  const createLinkMutation = useCreateLink();
  const deleteLinkMutation = useDeleteLink();
  const updateLinkStatusMutation = useUpdateLinkStatus();
  const updateLinkMutation = useUpdateLink();
  const links = linksData?.links || [];
  const brands: Brand[] = brandsData ? (brandsData as any).brands?.map((b: any) => ({
    id: b.id, 
    name: b.name, 
    logo: b.logo
  })) || [] : [];
  
  // Filter brands to only show those with links
  const brandsWithLinks = useMemo(() => {
    if (!links.length) return brands; // If no links, show all brands for creation
    
    // Get unique brand IDs from links
    const brandIds = [...new Set(links.map(link => link.brandId).filter(Boolean))];
    
    // Filter brands to only include those with links
    return brands.filter((brand: Brand) => brandIds.includes(brand.id));
  }, [brands, links]);

  useEffect(() => {
    document.title = t('links.title', 'AI Links Builder');
  }, [t]);

  // Filter links
  const filteredLinks = React.useMemo(() => {
    return links.filter(link => {
      // Brand filter: if selectedBrand is not an empty string (i.e., a specific brand is selected),
      // then filter by that brandId. Otherwise (selectedBrand is ''), include all brands.
      if (selectedBrand !== '' && link.brandId !== selectedBrand) return false;
      
      // Search filter
      const matchesSearch = !searchQuery || 
        link.oldURL.toLowerCase().includes(searchQuery.toLowerCase()) ||
        link.newURL.toLowerCase().includes(searchQuery.toLowerCase());
      
      return matchesSearch;
    });
  }, [links, searchQuery, selectedBrand]);

  const handleRequestDuplicate = (link: Link) => {
    setActiveLinkData(link);
    setIsEditMode(false);
    setIsCreateModalOpen(true);
  };

  const handleRequestEdit = (link: Link) => {
    setActiveLinkData(link);
    setIsEditMode(true);
    setIsCreateModalOpen(true);
  };

  const handleRequestQRCode = (link: Link) => {
    setQRCodeLink(link);
    setIsQRCodeModalOpen(true);
  };

  const handleCreateSmartLink = async (data: { destinationUrl: string; shortCode: string; brandId?: string }) => {
    try {
      // Construct the short URL from the code
      const shortURL = `https://go.socialness.ai/${data.shortCode}`;
      
      // Map form data to API format (corrected mapping)
      const apiData = {
        oldURL: data.destinationUrl, // API expects oldURL to be the original destination URL
        newURL: shortURL, // API expects newURL to be the new short URL (go.socialness.ai)
        brandId: data.brandId || ''
      };
      
      await createLinkMutation.mutateAsync(apiData, {
        onSuccess: () => refetchLinks(), // Refetch links on success
      });
      setIsCreateModalOpen(false);
      setActiveLinkData(null); // Reset active data
      setIsEditMode(false); // Reset mode
      
      // Show success message or handle success
    } catch (error) {
      console.error('Failed to create smart link:', error);
      // Handle error - you might want to show a toast or error message
    }
  };

  // New handler for updating an existing link
  const handleUpdateSmartLink = async (data: { destinationUrl: string; brandId?: string; shortCode: string /* slug, not changed */ }) => {
    if (!activeLinkData || !activeLinkData.id) {
      console.error("No active link data found for update.");
      return;
    }

    const payload: UpdateLinkPayload = {
      oldURL: data.destinationUrl, 
      brandId: data.brandId || '',
    };

    try {
      await updateLinkMutation.mutateAsync({ linkId: activeLinkData.id, payload });
      setIsCreateModalOpen(false);
      setActiveLinkData(null);
      setIsEditMode(false);
    } catch (err) {
      console.error('Failed to update smart link (in component):', err);
      // Potentially show an error toast to the user from here
    }
  };

  const handleDeleteLink = async (linkId: string) => {
    try {
      setDeletingLinkId(linkId); // Identify which link is being targeted
      await deleteLinkMutation.mutateAsync(linkId, {
        onSuccess: () => refetchLinks(), // Refetch links on success
      });
    } catch (error) {
      console.error('Failed to delete link:', error);
      // Handle error - you might want to show a toast or error message
    }
  };

  const handleUpdateLinkStatus = async (linkId: string, newStatus: LinkStatus) => {
    setUpdatingStatusLinkId(linkId);
    try {
      await updateLinkStatusMutation.mutateAsync({ linkId, status: newStatus }, {
        onSuccess: () => refetchLinks(), // Refetch links on success
      });
    } catch (error) {
      console.error(`Failed to update status to ${newStatus} for link ${linkId}:`, error);
    } finally {
      setUpdatingStatusLinkId(null);
    }
  };

  if (isLoading) {
    // Skeleton loader
    return (
      <div className="min-h-screen bg-white lg:ml-16 pt-16 lg:pt-0">
        <div className="w-[95%] mx-auto py-8">
          {/* Header skeleton */}
          <div className="mb-8 flex items-center justify-between">
            <div>
              <div className="h-8 bg-gray-200 rounded w-1/3 mb-2 animate-pulse"></div>
              <div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse"></div>
            </div>
            <div className="h-10 bg-gray-200 rounded-lg w-48 animate-pulse"></div>
          </div>
          
          {/* Analytics skeleton */}
          <div className="space-y-6 mb-8">
            {/* Metrics Cards skeleton */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="bg-white rounded-xl border border-black p-6 animate-pulse">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
                      <div className="h-8 bg-gray-200 rounded w-16"></div>
                    </div>
                    <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Chart skeleton */}
            <div className="bg-white rounded-xl border border-black p-6 animate-pulse">
              <div className="mb-6">
                <div className="h-6 bg-gray-200 rounded w-48 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-32"></div>
              </div>
              <div className="h-80 bg-gray-200 rounded"></div>
            </div>
          </div>
          
          {/* Search skeleton */}
          <div className="mb-6">
            <div className="h-10 bg-gray-200 rounded-lg w-96 animate-pulse"></div>
          </div>
          
          {/* Cards skeleton */}
          <div className="space-y-4">
            {Array.from({ length: 5 }).map((_, index) => (
              <div key={index} className="bg-white rounded-xl border border-gray-200 p-6 animate-pulse">
                <div className="flex items-center justify-between mb-4">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-6 bg-gray-200 rounded-full w-16"></div>
                </div>
                <div className="space-y-3">
                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                  <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                </div>
                <div className="flex items-center justify-between pt-4 mt-4 border-t border-gray-100">
                  <div className="flex gap-4">
                    <div className="h-4 bg-gray-200 rounded w-20"></div>
                    <div className="h-4 bg-gray-200 rounded w-32"></div>
                  </div>
                  <div className="h-4 bg-gray-200 rounded w-16"></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white lg:ml-16 pt-16 lg:pt-0">
        <div className="w-[95%] mx-auto py-8 flex items-center justify-center h-96">
          <div className="text-center">
            <p className="text-red-600 mb-4">{t('links.errorLoading', 'Failed to load links')}</p>
            <p className="text-gray-500">{error.message}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white lg:ml-16 pt-16 lg:pt-0">
      <div className="w-[95%] mx-auto py-8">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 font-title mb-2">
              {t('links.title', 'AI Links Builder')}
            </h1>
            <p className="mt-1 text-gray-500 font-body">
              {t('links.subtitle', 'Manage and track your shortened links')}
            </p>
          </div>
          <button 
            onClick={() => setIsCreateModalOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            {t('links.createSmartLink', 'Create a smart link')}
          </button>
        </div>

        {/* Analytics */}
        {linksData?.analytics && (
          <LinksMetrics 
            analytics={linksData.analytics} 
            totalLinks={linksData.count}
            totalClicks={linksData.TotalClicks}
          />
        )}

        {/* Search */}
        <div className="mb-6">
          <div className="flex gap-4 items-center">
            <div className="relative max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder={t('links.searchPlaceholder', 'Search links by URL...')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>
            
            <div className="w-64">
              <BrandDropdown
                brands={brandsWithLinks}
                selectedBrandId={selectedBrand}
                onBrandSelect={setSelectedBrand}
                showAllBrandsOption={true}
                showRedirectionCard={false}
              />
            </div>
          </div>
        </div>

        {/* Results count */}
        <div className="mb-6">
          <p className="text-sm text-gray-500">
            {t('links.resultsCount', {
              count: filteredLinks.length,
              total: links.length,
              defaultValue: 'Showing {{count}} of {{total}} links'
            })}
          </p>
        </div>

        {/* Links list */}
        {filteredLinks.length > 0 ? (
          <div className="space-y-4">
            {filteredLinks.map((linkItem) => (
              <LinkCard 
                key={linkItem.id} 
                link={linkItem} 
                onDelete={handleDeleteLink} 
                isDeleting={deleteLinkMutation.isPending && deletingLinkId === linkItem.id} 
                onDuplicate={handleRequestDuplicate}
                onEditRequest={handleRequestEdit}
                onQRCodeRequest={handleRequestQRCode}
                onUpdateStatus={handleUpdateLinkStatus}
                isUpdatingStatus={updateLinkStatusMutation.isPending && updatingStatusLinkId === linkItem.id}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <LinkIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {t('links.noResults', 'No links found')}
            </h3>
            <p className="text-gray-500 mb-6">
              {searchQuery
                ? t('links.noResultsFiltered', 'Try adjusting your search criteria')
                : t('links.noLinks', 'You haven\'t created any smart links yet')
              }
            </p>
            {!searchQuery && (
              <button 
                onClick={() => setIsCreateModalOpen(true)}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors mx-auto"
              >
                <Plus className="w-4 h-4" />
                {t('links.createFirstLink', 'Create your first smart link')}
              </button>
            )}
          </div>
        )}

        {/* Create Smart Link Modal */}
        <CreateSmartLinkModal
          isOpen={isCreateModalOpen}
          onClose={() => {
            setIsCreateModalOpen(false);
            setActiveLinkData(null);
            setIsEditMode(false);
          }}
          onSubmit={isEditMode ? handleUpdateSmartLink : handleCreateSmartLink}
          brands={brands}
          isCreating={createLinkMutation.isPending || updateLinkMutation.isPending}
          initialLinkData={activeLinkData}
          isEditMode={isEditMode}
        />

        {/* QR Code Modal */}
        <QRCodeModal
          isOpen={isQRCodeModalOpen}
          onClose={() => {
            setIsQRCodeModalOpen(false);
            setQRCodeLink(null);
          }}
          url={qrCodeLink?.newURL || ''}
          brandLogo={qrCodeLink?.brandLogo}
          brandName={qrCodeLink?.brandName}
        />
      </div>
    </div>
  );
};

export default Links; 